/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { SessionManagerType } from "client-code/video-provider-configurations";
import type { VideoProviderConfiguration } from "client-code/video-provider-configurations";

import { Log } from "../logging";

/**
 * Session-manager type values that identify a TiVo+ provider.
 * Compared case-insensitively. See {@link VideoProviderConfigModel.isTivoPlusProviderPartnerId}.
 */
const TIVO_PLUS_SESSION_MANAGER_TYPES: ReadonlyArray<string> = [
  SessionManagerType.TivoPlus,
  "trioSodiTiVoPlus"
];

/**
 * Synchronous mirror of the video-provider configurations, and the questions
 * asked of them about ipLinear / ipVod / nPvr / TiVo+ playback.
 *
 * **Why a mirror rather than reading storage directly:** `DrmManager` already
 * fetches these and caches them in
 * `Storage.Keys.VIDEO_PROVIDER_CONFIGURATIONS_DATA`, but `Storage.get()` is
 * Promise-based (IndexedDB) and several consumers are synchronous — most
 * notably the Redux selectors that decide whether a guide row shows the TiVo+
 * badge, and `ChannelsInteractor.isTivoPlusChannel` on the tuning path. Those
 * callers cannot await. This holds the same objects the storage read already
 * produced; it never fetches anything of its own and is not a second source of
 * truth.
 *
 * **Single writer.** `DrmManager` is the only component that populates this:
 * - `fetchVideoProviderConfiguration()` calls {@link primeAll} at startup.
 * - `resolveVpcObject()` calls {@link upsert} for providers fetched on demand,
 *   which keeps providers absent from the startup snapshot warm.
 *
 * Every other consumer is read-only.
 *
 * **Placement.** This lives outside `videoplayer/` deliberately. Provider
 * configuration is shared reference data that both `interactor/` and the
 * playback stack consume, and `videoplayer/` already imports `interactor/` —
 * so hosting it under `videoplayer/` forced the interactor to import
 * downwards and closed a module cycle. This module is a leaf: it may import
 * only `client-code` types and `../logging`, and nothing else.
 *
 * Classification is a relay of what the server declared about a provider,
 * never a client-side guess.
 */
export class VideoProviderConfigModel {
  private static readonly TAG: string = "VideoProviderConfigModel";

  /**
   * Provider ids whose raw session-manager type has already been logged, so a
   * surprising value is reported once rather than on every tune.
   */
  private static readonly oLoggedProviders: Set<string> = new Set<string>();

  private static oConfigs: Record<string, VideoProviderConfiguration> = {};

  private static oIsLoaded: boolean = false;

  // ── Population (DrmManager only) ───────────────────────────────────────────

  /**
   * Replace the mirror with the full set of provider configurations read back
   * from storage.
   *
   * Each entry is keyed by the `providerPartnerId` it carries, never by its
   * position or by whatever key the caller held it under. The service stores
   * these as a JSON **array**, so spreading the collection would key entries
   * by array index — every lookup by partner id would then miss. Reading the
   * id off each entry works for an array and for a keyed map alike, and stays
   * correct if the stored shape changes again.
   *
   * @param configs - Provider configurations, as an array or as a map. A
   *   `null`/`undefined` value is ignored so a failed storage read does not
   *   wipe a good mirror.
   */
  public static primeAll(
    configs:
      | ReadonlyArray<VideoProviderConfiguration>
      | Record<string, VideoProviderConfiguration>
      | null
      | undefined
  ): void {
    if (configs === null || configs === undefined) {
      Log.w(
        VideoProviderConfigModel.TAG,
        "primeAll() - ignoring empty configuration map"
      );
      return;
    }

    const entries: VideoProviderConfiguration[] = Object.values(configs);

    VideoProviderConfigModel.oConfigs = {};
    VideoProviderConfigModel.oIsLoaded = true;

    for (let index = 0; index < entries.length; index += 1) {
      VideoProviderConfigModel.upsert(entries[index]);
    }

    Log.i(
      VideoProviderConfigModel.TAG,
      "primeAll() - cached providers: " +
        String(Object.keys(VideoProviderConfigModel.oConfigs).length) +
        " of " +
        String(entries.length)
    );
  }

  /**
   * Add or replace a single provider entry.
   *
   * Entries arriving this way also mark the configuration loaded: a
   * per-provider fetch succeeding is sufficient evidence that configuration is
   * reachable, and callers gating on {@link isConfigurationLoaded} should not
   * defer once we hold a usable answer for the provider they asked about.
   *
   * @param config - The provider configuration to store. Ignored when it
   *   carries no `providerPartnerId` to key on.
   */
  public static upsert(
    config: VideoProviderConfiguration | null | undefined
  ): void {
    if (config === null || config === undefined) {
      return;
    }

    const providerPartnerId = config.providerPartnerId;

    if (
      providerPartnerId === undefined ||
      providerPartnerId === null ||
      providerPartnerId === ""
    ) {
      Log.w(
        VideoProviderConfigModel.TAG,
        "upsert() - configuration has no providerPartnerId"
      );
      return;
    }

    VideoProviderConfigModel.oConfigs[providerPartnerId] = config;
    VideoProviderConfigModel.oIsLoaded = true;
  }

  /** Drop every mirrored entry. Used by tests. */
  public static clear(): void {
    VideoProviderConfigModel.oConfigs = {};
    VideoProviderConfigModel.oIsLoaded = false;
  }

  // ── Queries ────────────────────────────────────────────────────────────────

  /**
   * Whether the given provider is configured as a TiVo+ session manager.
   *
   * @param providerPartnerId - The channel's `linearProviderPartnerId`.
   * @returns `true` only when configuration for the provider is present and
   *   declares a TiVo+ session-manager type. An unknown provider returns
   *   `false`; callers that must distinguish "not TiVo+" from "not loaded
   *   yet" should consult {@link isConfigurationLoaded} first.
   */
  public static isTivoPlusProviderPartnerId(
    providerPartnerId: string | undefined | null
  ): boolean {
    const sessionManagerType =
      VideoProviderConfigModel.getSessionManagerType(providerPartnerId);

    if (sessionManagerType === undefined) {
      return false;
    }

    const normalised = sessionManagerType.toLowerCase();
    const isTivoPlus = TIVO_PLUS_SESSION_MANAGER_TYPES.some(
      (candidate) => candidate.toLowerCase() === normalised
    );

    VideoProviderConfigModel.logSessionManagerTypeOnce(
      providerPartnerId as string,
      sessionManagerType,
      isTivoPlus
    );

    return isTivoPlus;
  }

  /**
   * Whether configuration for this specific provider is available.
   *
   * Distinct from {@link isTivoPlusProviderPartnerId}, which answers `false`
   * both for "this provider is not TiVo+" and for "this provider is unknown".
   * Callers that must not act on a guess — playback entry points, which would
   * otherwise tune a TiVo+ channel as though it were ordinary linear and send
   * an unresolved template to the CDN — need the two cases separated.
   *
   * @param providerPartnerId - The channel's `linearProviderPartnerId`.
   * @returns `true` when the provider has a configuration entry.
   */
  public static hasConfiguration(
    providerPartnerId: string | undefined | null
  ): boolean {
    return (
      VideoProviderConfigModel.getConfiguration(providerPartnerId) !== undefined
    );
  }

  /**
   * Whether any provider configuration has been mirrored yet.
   *
   * Used to defer a decision on a cold start instead of answering wrongly.
   */
  public static isConfigurationLoaded(): boolean {
    return VideoProviderConfigModel.oIsLoaded;
  }

  /**
   * Whether the provider forbids fast tune — playing the pre-session
   * (templated / `tuningUri`) URL while the streaming session is still being
   * created.
   *
   * `disableFastTune` is optional on the configuration and the service
   * documents its absence as `false` ("Assume false when missing"), so
   * anything other than an explicit `true` leaves fast tune enabled. That is
   * deliberately also the answer for an unknown provider and for a cold start
   * where nothing has been mirrored yet: the gate closes only on a positive
   * declaration, never on a gap, so a configuration that has not loaded can
   * never silently cost every channel its fast tune.
   *
   * @param providerPartnerId - The channel's `linearProviderPartnerId`.
   * @returns `true` only when the provider's configuration declares
   *   `disableFastTune: true`.
   */
  public static isFastTuneDisabled(
    providerPartnerId: string | undefined | null
  ): boolean {
    const config = VideoProviderConfigModel.getConfiguration(providerPartnerId);

    return config !== undefined && config.disableFastTune === true;
  }

  /**
   * The configured session-manager type for a provider, verbatim.
   *
   * @returns The raw value, or `undefined` when the provider is unknown or
   *   carries no session-manager configuration.
   */
  private static getSessionManagerType(
    providerPartnerId: string | undefined | null
  ): string | undefined {
    const config = VideoProviderConfigModel.getConfiguration(providerPartnerId);

    if (
      config === undefined ||
      config.sessionManagerConfig === undefined ||
      config.sessionManagerConfig === null
    ) {
      return undefined;
    }

    return config.sessionManagerConfig.sessionManagerType;
  }

  /**
   * The provider-specific session-manager base URL, when one is configured.
   *
   * Absent means the client should use its default session endpoint.
   */
  public static getSessionManagerUrl(
    providerPartnerId: string | undefined | null
  ): string | undefined {
    const config = VideoProviderConfigModel.getConfiguration(providerPartnerId);

    if (
      config === undefined ||
      config.sessionManagerConfig === undefined ||
      config.sessionManagerConfig === null
    ) {
      return undefined;
    }

    const url = config.sessionManagerConfig.sessionManagerUrl;

    return url === null || url === undefined || url === "" ? undefined : url;
  }

  /**
   * Whether the provider requires a streaming session for VOD previews
   * (trailers). When it does not, a preview plays its `assetUrl` directly,
   * without DRM or a session.
   *
   * An unknown provider, or one carrying no session-manager configuration,
   * answers `false` — matching Kaveri's `checkIsNeedSessionCreateForPreviews`.
   *
   * @param providerPartnerId - The VOD watch action's `partnerId`.
   * @returns `true` only when the configuration declares
   *   `needSessionCreateForPreviews: true`.
   */
  public static needsSessionCreateForPreviews(
    providerPartnerId: string | undefined | null
  ): boolean {
    const config = VideoProviderConfigModel.getConfiguration(providerPartnerId);

    if (
      config === undefined ||
      config.sessionManagerConfig === undefined ||
      config.sessionManagerConfig === null
    ) {
      return false;
    }

    return config.sessionManagerConfig.needSessionCreateForPreviews === true;
  }

  /**
   * The URI scheme the provider requires playback URLs to use (`http` or
   * `https`), when it overrides whatever the channel or session supplied.
   */
  public static getUriSchemeOverride(
    providerPartnerId: string | undefined | null
  ): string | undefined {
    const config = VideoProviderConfigModel.getConfiguration(providerPartnerId);

    if (
      config === undefined ||
      config.sessionManagerConfig === undefined ||
      config.sessionManagerConfig === null
    ) {
      return undefined;
    }

    const scheme = config.sessionManagerConfig.uriSchemeOverride;

    return scheme === null || scheme === undefined || scheme === ""
      ? undefined
      : scheme;
  }

  /**
   * The DRM server type configured for the provider, as a plain string
   * suitable for matching against a tuning URI's DRM segment.
   *
   * @returns The configured value, or `undefined` when the provider has no
   *   DRM configuration — which is the normal case for unencrypted TiVo+
   *   streams and must not be defaulted to a real DRM type.
   */
  public static getDrmTypeById(
    providerPartnerId: string | undefined | null
  ): string | undefined {
    const config = VideoProviderConfigModel.getConfiguration(providerPartnerId);

    if (
      config === undefined ||
      config.drmConfig === undefined ||
      config.drmConfig === null
    ) {
      return undefined;
    }

    const drmServerType = config.drmConfig.drmServerType;

    if (drmServerType === null || drmServerType === undefined) {
      return undefined;
    }

    const asString = String(drmServerType);

    return asString === "" ? undefined : asString;
  }

  /**
   * The transport encoding types configured for the provider.
   *
   * @returns The configured list, or an empty array when unknown. Callers
   *   supply their own default rather than receiving one here.
   */
  public static getTransportEncodingTypesById(
    providerPartnerId: string | undefined | null
  ): string[] {
    const config = VideoProviderConfigModel.getConfiguration(providerPartnerId);

    if (
      config === undefined ||
      config.drmConfig === undefined ||
      config.drmConfig === null
    ) {
      return [];
    }

    const encodingTypes = config.drmConfig.transportEncodingType;

    if (
      encodingTypes === undefined ||
      encodingTypes === null ||
      encodingTypes.length === 0
    ) {
      return [];
    }

    return encodingTypes.map((encodingType) => String(encodingType));
  }

  /** Clears the one-time log bookkeeping. Used by tests. */
  public static resetLogState(): void {
    VideoProviderConfigModel.oLoggedProviders.clear();
  }

  /**
   * Look up one provider configuration.
   *
   * @param providerPartnerId - The channel's `linearProviderPartnerId`.
   * @returns The configuration, or `undefined` when unknown.
   */
  private static getConfiguration(
    providerPartnerId: string | undefined | null
  ): VideoProviderConfiguration | undefined {
    if (
      providerPartnerId === undefined ||
      providerPartnerId === null ||
      providerPartnerId === ""
    ) {
      return undefined;
    }

    return VideoProviderConfigModel.oConfigs[providerPartnerId];
  }

  /**
   * Report a provider's raw session-manager type the first time it is seen,
   * so an unrecognised spelling is visible in logs rather than silently
   * disabling TiVo+ handling for that provider.
   */
  private static logSessionManagerTypeOnce(
    providerPartnerId: string,
    sessionManagerType: string,
    isTivoPlus: boolean
  ): void {
    if (VideoProviderConfigModel.oLoggedProviders.has(providerPartnerId)) {
      return;
    }

    VideoProviderConfigModel.oLoggedProviders.add(providerPartnerId);

    Log.i(
      VideoProviderConfigModel.TAG,
      "sessionManagerType for provider " +
        providerPartnerId +
        " is '" +
        sessionManagerType +
        "' (tivoPlus=" +
        String(isTivoPlus) +
        ")"
    );
  }
}
